/**
 * @file PreviewControl.tsx
 * @module PreviewControl
 * @description
 *   Generic control component that renders UI controls based on configuration.
 *   Supports DROPDOWN and BUTTON_GROUP control types.
 *   This component enables configuration-driven preview controls without
 *   hardcoded device-specific logic.
 *
 * @example
 * // Dropdown control
 * <PreviewControl
 *   control={{
 *     type: PreviewControlType.DROPDOWN,
 *     key: 'size',
 *     label: 'Size',
 *     options: [{ label: '10"', value: '10"' }],
 *     defaultValue: '10"'
 *   }}
 *   value="10""
 *   onChange={(value) => handleChange('size', value)}
 * />
 *
 * @example
 * // Button group control
 * <PreviewControl
 *   control={{
 *     type: PreviewControlType.BUTTON_GROUP,
 *     key: 'orientation',
 *     label: 'Orientation',
 *     options: [
 *       { label: 'Portrait', value: 'Portrait' },
 *       { label: 'Landscape', value: 'Landscape' }
 *     ],
 *     defaultValue: 'Landscape'
 *   }}
 *   value="Landscape"
 *   onChange={(value) => handleChange('orientation', value)}
 * />
 */

import { memo } from 'react';
import { KatButton, KatDropdown } from '@amzn/katal-react';
import {
  PreviewControlConfig,
  PreviewControlKey,
  PreviewControlType,
  PreviewControlValue,
} from 'src/configs/previewConfigurations/types';
import { logger } from 'src/logger';
import styles from './PreviewControl.module.css';

/**
 * Props for the PreviewControl component
 * @interface PreviewControlProps
 * @property {PreviewControlConfig} control - Control configuration defining type, options, etc.
 * @property {string} value - Current selected value
 * @property {(value: string) => void} onChange - Callback when value changes
 * @property {Record<string, string>} controlValues - All current control values for dynamic options
 */
interface PreviewControlProps {
  control: PreviewControlConfig;
  value: string;
  onChange: (value: string) => void;
  controlValues: Record<PreviewControlKey, PreviewControlValue>;
}

/**
 * Generic control component that renders based on control type
 * @component PreviewControl
 * @param {PreviewControlProps} props - Component props
 * @returns {JSX.Element | null} Rendered control or null for unknown types
 */
export const PreviewControl = memo(
  ({ control, value, onChange, controlValues }: PreviewControlProps) => {
    // Resolve options (static array or dynamic function)
    const resolvedOptions =
      typeof control.options === 'function' ? control.options(controlValues) : control.options;

    switch (control.type) {
      case PreviewControlType.DROPDOWN:
        return (
          <div>
            <label className={styles.label}>{control.label}</label>
            <div className={styles.dropdownContainer}>
              <KatDropdown
                value={value}
                onChange={(event: KatDropdown.ChangeEvent) => onChange(event.detail.value)}
                options={resolvedOptions.map((opt) => ({
                  name: opt.label,
                  value: opt.value,
                }))}
                placeholder={`Select ${control.label.toLowerCase()}`}
                data-testid={`${control.key}-select`}
              />
            </div>
          </div>
        );

      case PreviewControlType.BUTTON_GROUP:
        return (
          <div>
            <label className={styles.label}>{control.label}</label>
            <div className={styles.buttonGroup}>
              {resolvedOptions.map((option) => (
                <KatButton
                  key={option.value}
                  variant={value === option.value ? 'primary' : 'secondary'}
                  onClick={() => onChange(option.value)}
                  data-testid={`preview-button-${option.value.toLowerCase()}`}
                >
                  {option.label}
                </KatButton>
              ))}
            </div>
          </div>
        );

      default:
        logger.warn(`Unknown control type: ${control.type}`);
        return null;
    }
  },
);

PreviewControl.displayName = 'PreviewControl';
